/**
 * Labels and descriptions for the action filter fields.
 *
 * Temporary Hold (`SEND_DELAY`) and Request Approval expose the very same
 * controls as the attachment-processing actions, but there the filter decides
 * whether the rule runs at all instead of which attachments get processed.
 * The wording inherited from Protect Attachments ("will be saved as
 * attachments", "only process files") read as misleading in those two
 * applications, so each variant keeps its own copy here rather than spreading
 * conditionals over the field components (MXH-5760).
 *
 * Adding or dropping a field for one variant only means editing its map: the
 * components ask for a label by field name and never for a translation key.
 */
import { useTranslation } from "react-i18next";

import { useRuleContext } from "../../context/ruleContext";
import { FilterName } from "./types";

import { RuleType } from "pages/Rules/common/types";

export type FilterCopyName = Exclude<FilterName, "filterPolicyId">;

type FieldCopy = { label: string; desc: string };
type CopyMap = Record<FilterCopyName, FieldCopy>;

/** Rules where the action filter gates the whole rule, not the attachments. */
const attachmentVariantTypes: RuleType[] = ["SEND_DELAY", "REQUEST_APPROVAL"];

const actionCopy: CopyMap = {
  attachmentSize: {
    label: "filterAttachmentsBySize",
    desc: "filterAttachmentsBySizeDescription",
  },
  includeAttachmentExtensions: {
    label: "actionFilters.includeAttachmentExtensions.label",
    desc: "actionFilters.includeAttachmentExtensions.desc",
  },
  excludeAttachmentExtensions: {
    label: "actionFilters.excludeAttachmentExtensions.label",
    desc: "actionFilters.excludeAttachmentExtensions.desc",
  },
  includeAttachmentNames: {
    label: "actionFilters.includeAttachmentNames.label",
    desc: "actionFilters.includeAttachmentNames.desc",
  },
  excludeAttachmentNames: {
    label: "actionFilters.excludeAttachmentNames.label",
    desc: "actionFilters.excludeAttachmentNames.desc",
  },
  includeAttachmentTypes: {
    label: "actionFilters.includeAttachmentTypes.label",
    desc: "actionFilters.includeAttachmentTypes.desc",
  },
  excludeAttachmentTypes: {
    label: "actionFilters.excludeAttachmentTypes.label",
    desc: "actionFilters.excludeAttachmentTypes.desc",
  },
  excludeInlineImages: {
    label: "excludeInlineImages",
    desc: "excludeInlineImagesDescription",
  },
  includeZipWithPassword: {
    label: "includeZipWithPasswordLabel",
    desc: "includeZipWithPasswordDesc",
  },
  includeRfc822: {
    label: "includeRfc822Label",
    desc: "includeRfc822Desc",
  },
  excludeMultipartReport: {
    label: "excludeMultipartReportLabel",
    desc: "excludeMultipartReportDesc",
  },
  includeTnef: {
    label: "includeTnefLabel",
    desc: "includeTnefDesc",
  },
};

const attachmentCopy: CopyMap = buildAttachmentCopy();

export function useFilterCopy() {
  const { t } = useTranslation("rules");
  const { state } = useRuleContext();
  const isAttachmentVariant = attachmentVariantTypes.includes(state.type);
  const copy = isAttachmentVariant ? attachmentCopy : actionCopy;

  return {
    isAttachmentVariant,
    label: (name: FilterCopyName) => t(copy[name].label),
    desc: (name: FilterCopyName) => t(copy[name].desc),
  };
}

/**
 * Every field keeps its name under `attachmentFilters.<field>`, so the map is
 * derived from the field list instead of spelled out key by key.
 */
function buildAttachmentCopy(): CopyMap {
  const names = Object.keys(actionCopy) as FilterCopyName[];

  return names.reduce((acc, name) => {
    acc[name] = {
      label: `attachmentFilters.${name}.label`,
      desc: `attachmentFilters.${name}.desc`,
    };
    return acc;
  }, {} as CopyMap);
}
